import { getCsrfToken } from "../api/client";
import type { TelemetryName, TypedEvent } from "./catalog";
import { redactProps } from "./redact";

// Same-origin, batched, redaction-guarded telemetry. A failure here must NEVER block
// or surface to a product action (G-15) — everything is fire-and-forget and swallowed.

const MAX_BATCH = 50; // API accepts at most 50 events/request.
const FLUSH_MS = 4000;

interface QueuedEvent {
  name: TelemetryName;
  properties: Record<string, string | number | boolean | null | string[]>;
  occurredAt: string;
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let enabled = true;

export function setTelemetryEnabled(value: boolean): void {
  enabled = value;
}

export function track(event: TypedEvent): void {
  if (!enabled) return;
  const { name, ...rest } = event;
  queue.push({ name, properties: redactProps(rest as Record<string, unknown>), occurredAt: new Date().toISOString() });
  if (queue.length >= MAX_BATCH) {
    void flush();
  } else if (!timer) {
    timer = setTimeout(() => void flush(), FLUSH_MS);
  }
}

export async function flush(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!queue.length) return;
  // Check the token BEFORE draining: pre-auth events (SSO discovery, login attempts)
  // must stay queued until a session establishes a CSRF token, not be dropped.
  const csrf = getCsrfToken();
  if (!csrf) return;
  const events = queue.splice(0, MAX_BATCH);
  try {
    await fetch("/v1/telemetry", {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "content-type": "application/json", "x-csrf-token": csrf },
      body: JSON.stringify({ events })
    });
  } catch {
    // Never block product actions on telemetry failure. Dropped batches are acceptable.
  }
}

// Flush best-effort as the page is hidden/unloaded.
export function installTelemetryLifecycle(): void {
  if (typeof document === "undefined") return;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush();
  });
  window.addEventListener("pagehide", () => void flush());
}
